'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { AppBar, Toolbar, Container, Grid, IconButton } from '@mui/material'
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material'
import SearchIcon from '@mui/icons-material/Search'

// Types
import type { AppBarProps } from '@/types/components/templates/app-bar'

// Components
const DrawerOrganism = dynamic(() => import('@/components/organisms/drawer'))
const MenuOrganism = dynamic(() => import('@/components/organisms/menu'))
const LangButtonAtom = dynamic(() => import('@/components/atoms/buttons/lang'))
const LogoShapeAtom = dynamic(() => import('@/components/atoms/shapes/logo'))

const AppBarTemplate = (props: AppBarProps) => {
	// Props
	const { lng, menus, mode } = props

	// Variables
	const [open, setOpen] = useState<boolean>(false)

	return (
		<AppBar elevation={0} sx={{ bgcolor: mode === 'white' ? 'common.white' : '#404786' }}>
			<Toolbar sx={{ minHeight: { lg: 80, xl: 100 } }}>
				<Container maxWidth="xl">
					<Grid container size={12} justifyContent="space-between" alignItems="center">
						<Grid size="auto">
							<Grid container>
								<Grid
									sx={{
										display: 'inline-flex',
										'& a': {
											display: 'inline-flex',
											'& svg': {
												width: 'auto',
												height: { xs: 44, lg: 54, xl: 60 }
											}
										}
									}}
								>
									<Link href={`/${lng}`}>
										<LogoShapeAtom mode={mode === 'white' ? 'original' : 'white'} />
									</Link>
								</Grid>

								<Grid
									display={{ xs: 'none', md: 'inline-flex' }}
									sx={{
										height: 'auto',
										ml: { xs: 2, md: 3, lg: 4, xl: 5 },
										'& .MuiList-root': {
											flexWrap: 'wrap',
											gap: { xs: 2, lg: 4, xl: 5 },
											'& .MuiMenuItem-root': {
												p: 0,
												'& a': {
													width: 1,
													'& button': {
														width: 1,
														px: { xs: 2, md: 3, lg: 5, xl: 6 },
														fontSize: 18,
														color: mode === 'white' ? 'common.black' : 'common.white',
														bgcolor: 'transparent',
														borderRadius: 0
													}
												},
												'&:not(.selected) .MuiTypography-root': { color: mode === 'white' ? 'common.black' : 'common.white' }
											}
										}
									}}
								>
									<MenuOrganism lng={lng} items={menus} />
								</Grid>
							</Grid>
						</Grid>

						<Grid size="auto">
							<Grid container spacing={1}>
								<Grid display={{ md: 'none' }}>
									<IconButton onClick={() => setOpen(!open)} sx={{ ...(mode !== 'white' && { color: 'white' }) }}>
										<MenuIcon />
									</IconButton>
								</Grid>

								<Grid display={{ xs: 'none', md: 'block' }}>
									<IconButton component={Link} href={`/${lng}/explore`} sx={{ ...(mode !== 'white' && { color: 'white' }) }}>
										<SearchIcon />
									</IconButton>
								</Grid>

								<Grid
									display={{ xs: 'none', md: 'block' }}
									sx={{
										...(mode !== 'white' && {
											'& .MuiButtonBase-root': { color: 'common.white' },
											'& .MuiDivider-root': { bgcolor: 'common.white' }
										})
									}}
								>
									<LangButtonAtom lng={lng} />
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Container>
			</Toolbar>

			<DrawerOrganism
				open={open}
				setOpen={setOpen}
				sx={{
					display: { md: 'none' },
					'.MuiPaper-root': { width: 1 }
				}}
			>
				<Container maxWidth="md">
					<Toolbar
						sx={{
							justifyContent: 'space-between',
							minHeight: { lg: 80, xl: 100 }
						}}
					>
						<Grid size="auto">
							<LangButtonAtom lng={lng} />
						</Grid>

						<Grid size="auto">
							<IconButton color="error" onClick={() => setOpen(!open)}>
								<CloseIcon />
							</IconButton>
						</Grid>
					</Toolbar>

					<Grid
						container
						display={{ md: 'none' }}
						justifyContent="space-between"
						alignItems="center"
						px={4}
						sx={{
							'& .MuiList-root': {
								width: 1,
								flexWrap: 'wrap',
								'& .MuiMenuItem-root': {
									width: 1,
									p: 0,
									borderBottom: '1px solid',
									borderBottomColor: 'divider',
									'&:last-child': { borderBottom: 'none' },
									'& a': {
										width: 1,
										'& button': {
											justifyContent: 'flex-start',
											width: 1,
											py: 1,
											fontSize: 16,
											textAlign: 'inherit',
											color: 'common.black',
											bgcolor: 'transparent',
											borderRadius: 0,
											'& .MuiTypography-root': { color: 'common.black' }
										}
									}
								}
							}
						}}
					>
						<MenuOrganism lng={lng} items={menus} setOpen={setOpen} />
					</Grid>
				</Container>
			</DrawerOrganism>
		</AppBar>
	)
}

export default AppBarTemplate
